import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { isNotFound } from "@/api/perkpoint/client";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogOut, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  scopeRedirectFor,
  usePerkPointScope,
} from "@/pages/perkpoint/programs/program-scope";

/** Keeps Perk Point routes inside the current admin/manager scope. */
export function PerkPointScopeGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { mode, session, exitProgram, programBound } = usePerkPointScope();
  // A manager session can outlive its program (deleted, or a reset dev DB).
  const managed = useQuery({
    queryKey: programKeys.detail(session?.programId ?? ""),
    queryFn: () => ProgramsApi.get(session!.programId),
    enabled: mode === "manager" && Boolean(session),
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
  useEffect(() => {
    if (managed.error && isNotFound(managed.error)) {
      if (programBound) {
        toast.error("Your assigned program is no longer available.");
        return;
      }
      exitProgram();
      toast.error("That program no longer exists. Back to all programs.");
    }
  }, [managed.error, exitProgram, programBound]);
  const redirect = scopeRedirectFor(pathname, mode, session);
  if (redirect) return <Navigate to={redirect} replace />;
  return <>{children}</>;
}

/** Shown while a portfolio admin is logged in as manager of one program. */
export function PerkPointManagerBanner() {
  const navigate = useNavigate();
  const { mode, session, userName, exitProgram, programBound } =
    usePerkPointScope();

  // Portfolio admins temporarily acting as manager — not program-bound roles.
  if (mode !== "manager" || !session || programBound) return null;

  const exit = () => {
    exitProgram();
    navigate("/perkpoint/program/setup");
    toast.success(`Left ${session.programName}. Back to all programs.`);
  };

  return (
    <div
      role="status"
      className="border-b border-amber-200 bg-amber-50 px-5 py-2.5 lg:px-7"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex min-w-0 items-start gap-2 text-sm text-amber-950">
          <UserCog className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <span className="min-w-0">
            You are logged in as <strong>{userName}</strong>, managing{" "}
            <strong>{session.programName}</strong>
            {session.programCode ? (
              <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                {session.programCode}
              </span>
            ) : null}{" "}
            as program manager.
          </span>
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 shrink-0 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
          onClick={exit}
        >
          <LogOut className="mr-1.5 h-3.5 w-3.5" />
          Exit manager view
        </Button>
      </div>
    </div>
  );
}
